import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Tooltip, Empty, Input, Button, Spin, Pagination, Table } from 'antd';
import { ClusterOutlined, WarningFilled } from '@ant-design/icons';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { liaisonService } from '../../services/liaisonService';
import { formatDate, can, STATUT_MAP } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { TYPE_LIAISON_META, sigleTypeLiaison } from '../../utils/typeLiaison';
import { clesDepliees, liaisonADossierMoinsAvance } from '../../utils/liaisonsAffichage';

const GREEN = '#1a6b3c';
const MUTED = '#6b7280';

/* Gouttiere des connecteurs, et largeur du numero dans le sous-tableau. Les
   deux valeurs sont partagees par l'en-tete et par les lignes : c'est ce qui
   fait tenir l'alignement des colonnes, et les separer les ferait deriver l'un
   par rapport a l'autre au premier changement. */
const GOUTTIERE = 26;
const LARGEUR_NUMERO = 150;

/**
 * Regroupements de dossiers issus d'un même accident.
 *
 * UN TABLEAU DE GROUPES, ET UN SOUS-TABLEAU PAR GROUPE. Même forme que les
 * règlements : la ligne porte le regroupement, la ligne dépliée porte ses
 * dossiers, avec ses propres en-têtes de colonnes.
 *
 * POURQUOI CE N'EST PAS LE COMPOSANT `Tree` D'ANT DESIGN NON PLUS. Il attend
 * des nœuds homogènes, rendus comme un titre unique, alors que nos branches
 * portent trois informations à aligner d'une ligne à l'autre - numéro, statut,
 * rédacteur. C'était déjà la raison de ne pas l'employer ; le sous-tableau
 * répond mieux à cette objection que les bordures CSS qui l'avaient remplacé,
 * puisqu'il nomme les colonnes au lieu de laisser deviner ce que chaque valeur
 * représente.
 *
 * LES CONNECTEURS RESTENT DESSINÉS EN BORDURES. Ils disent quelque chose que
 * des colonnes ne disent pas : ces dossiers dépendent du regroupement au-dessus.
 *
 * POURQUOI LES GROUPES S'OUVRENT DÉPLIÉS. Le contenu du groupe est exactement
 * ce qu'on vient consulter sur cette page. Le repli reste offert, ligne à ligne.
 */
export default function SinistresLies() {
  const { t } = useTranslation('sinistres');
  const navigate = useNavigate();
  const [liaisons, setLiaisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  /* Les lignes ouvertes sont portées par la CLÉ de la liaison, jamais par son
     rang : la pagination réordonne les lignes, et un repli indexé sur la
     position aurait suivi la place plutôt que le groupe. */
  const [clesOuvertes, setClesOuvertes] = useState([]);
  const peutLier = can(HAB.SINISTRE_LIER);

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res = await liaisonService.page({ page });
      const recues = Array.isArray(res.data?.data) ? res.data.data : [];
      setLiaisons(recues);
      setClesOuvertes(clesDepliees(recues));
      setPagination((prev) => ({ ...prev, current: page + 1, total: res.data?.totalElements || 0 }));
    } catch {
      setLiaisons([]);
      setClesOuvertes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  const colonnes = [
    {
      title: t('sinistresLies.colonnes.regroupement'),
      key: 'regroupement',
      render: (_, l) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <strong style={{ color: GREEN, fontSize: 14 }}>{l.numeroLiaison}</strong>
          <Tag
            color={TYPE_LIAISON_META[l.typeSinistre]?.color || 'default'}
            style={{ borderRadius: 20, fontSize: 11, margin: 0 }}
          >
            {sigleTypeLiaison(l.typeSinistre)}
          </Tag>
        </div>
      ),
    },
    {
      title: t('sinistresLies.colonnes.dossiers'),
      key: 'dossiers',
      width: 120,
      render: (_, l) => (
        <span style={{ fontSize: 12.5, color: MUTED }}>
          {t('sinistresLies.colonnes.nbDossiers', { count: l.nombreDossiers ?? 0 })}
        </span>
      ),
    },
    {
      title: t('sinistresLies.colonnes.derniereAction'),
      key: 'derniereAction',
      width: 160,
      render: (_, l) => <span style={{ fontSize: 12, color: MUTED }}>{formatDate(l.derniereAction)}</span>,
    },
    {
      title: '',
      key: 'alerte',
      width: 44,
      align: 'center',
      render: (_, l) => (liaisonADossierMoinsAvance(l) ? (
        <Tooltip title={t('sinistresLies.alerteRegroupement')}>
          <WarningFilled style={{ color: '#d97706' }} />
        </Tooltip>
      ) : null),
    },
  ];

  /* Le récit décrit le GROUPE : il se range sous les dossiers, à l'aplomb des
     branches, et non dans l'une d'elles. */
  const rendreDossiers = (liaison) => (
    <div style={{ padding: '2px 0 10px 0', background: '#fff' }}>
      <EnteteDossiers />
      {(liaison.membres || []).map((m, i) => (
        <Branche
          key={m.sinistreTrackingId || m.numeroSinistreLocal}
          membre={m}
          derniere={i === (liaison.membres || []).length - 1}
          onOuvrir={(id) => navigate(`/sinistres/${id}`)}
        />
      ))}
      <div style={{ paddingLeft: GOUTTIERE, marginTop: 14 }}>
        <NouvellesCirconstances
          liaison={liaison}
          peutModifier={peutLier}
          onEnregistre={() => load(pagination.current - 1)}
        />
      </div>
    </div>
  );

  return (
    <Layout title={t('sinistresLies.titre')}>
      <PageHeader
        titre={t('sinistresLies.titre')}
        icone={<ClusterOutlined />}
        compte={pagination.total}
        unite={t('sinistresLies.unite')}
        unitePluriel={t('sinistresLies.unitePluriel')}
      />

      {loading ? (
        <div className="cb-card" style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : liaisons.length === 0 ? (
        <div className="cb-card" style={{ padding: 40 }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('sinistresLies.aucuneLiaison')} />
        </div>
      ) : (
        <>
          <div className="cb-card" style={{ padding: 0, overflow: 'hidden' }}>
            <Table
              rowKey="liaisonTrackingId"
              columns={colonnes}
              dataSource={liaisons}
              size="middle"
              // La pagination reste celle de la page, sous le tableau : elle
              // pilote le chargement serveur, alors que celle du tableau ne
              // decouperait que les lignes deja recues.
              pagination={false}
              expandable={{
                expandedRowKeys: clesOuvertes,
                onExpandedRowsChange: setClesOuvertes,
                expandedRowRender: rendreDossiers,
                // Un regroupement sans dossier n'a rien a deplier : offrir la
                // fleche donnerait un clic qui n'ouvre rien.
                rowExpandable: (l) => (l.membres || []).length > 0,
              }}
            />
          </div>

          {pagination.total > pagination.pageSize && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger={false}
                onChange={(p) => { setPagination((prev) => ({ ...prev, current: p })); load(p - 1); }}
              />
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

/* ── En-têtes du sous-tableau ───────────────────────────────────────────── */

/**
 * Les colonnes des dossiers, nommées.
 *
 * C'est ce que l'affichage précédent n'avait pas : trois valeurs alignées dont
 * rien ne disait ce qu'elles étaient. Un statut se reconnaît, un nom de
 * rédacteur beaucoup moins - surtout quand il est vide.
 *
 * La structure reprend exactement celle de `Branche` : même gouttière, mêmes
 * largeurs, même `marginLeft: auto`. Les faire diverger ferait glisser les
 * en-têtes par rapport aux valeurs qu'ils nomment.
 */
function EnteteDossiers() {
  const { t } = useTranslation('sinistres');
  const style = {
    fontSize: 10.5, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: 0.3,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: GOUTTIERE, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px' }}>
        <span style={{ ...style, minWidth: LARGEUR_NUMERO }}>{t('sinistresLies.entete.numero')}</span>
        <span style={style}>{t('sinistresLies.entete.statut')}</span>
        <span style={{ ...style, marginLeft: 'auto' }}>{t('sinistresLies.entete.redacteur')}</span>
        {/* Réserve la place de l'icône d'alerte, pour que la colonne
            « Rédacteur » ne se décale pas selon qu'un dossier est en arrière. */}
        <span style={{ width: 14, flexShrink: 0 }} />
      </div>
    </div>
  );
}

/* ── Une ligne de dossier ───────────────────────────────────────────────── */

/**
 * Un dossier du regroupement.
 *
 * Les connecteurs sont deux bordures : une verticale qui descend depuis le
 * regroupement, une horizontale qui rejoint la ligne. La dernière coupe la
 * verticale à mi-hauteur, sinon le trait continuerait dans le vide sous le
 * dernier dossier.
 */
function Branche({ membre, derniere, onOuvrir }) {
  const { t } = useTranslation('sinistres');
  const statut = STATUT_MAP[membre.statut] || { label: membre.statut || '-', color: 'default' };
  const numero = membre.numeroSinistreLocal || membre.sinistre?.numeroSinistreLocal || '-';

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <div style={{ width: GOUTTIERE, position: 'relative', flexShrink: 0 }}>
        <span style={{
          position: 'absolute', left: 9, top: 0,
          bottom: derniere ? '50%' : 0,
          borderLeft: '1px solid #d9e2dc',
        }}
        />
        <span style={{
          position: 'absolute', left: 9, top: '50%', width: 12,
          borderTop: '1px solid #d9e2dc',
        }}
        />
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => membre.sinistreTrackingId && onOuvrir(membre.sinistreTrackingId)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && membre.sinistreTrackingId) {
            e.preventDefault();
            onOuvrir(membre.sinistreTrackingId);
          }
        }}
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
          padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f8f5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', minWidth: LARGEUR_NUMERO }}>
          {numero}
        </span>
        <Tag color={statut.color} style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
          {statut.label}
        </Tag>
        <span style={{
          fontSize: 12, color: MUTED, marginLeft: 'auto',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        >
          {membre.redacteurNom || t('sinistresLies.nonAffecte')}
        </span>
        <span style={{ width: 14, flexShrink: 0, textAlign: 'right' }}>
          {membre.moinsAvance && (
            <Tooltip title={t('sinistresLies.alerteDossier')}>
              <WarningFilled style={{ color: '#d97706' }} />
            </Tooltip>
          )}
        </span>
      </div>
    </div>
  );
}

/* ── Récit du regroupement ──────────────────────────────────────────────── */

/**
 * VIDER ENREGISTRE UN EFFACEMENT, et c'est la différence avec le rapprochement :
 * là, un champ vide veut dire « je n'ai rien à ajouter » et ne doit pas détruire
 * ce qu'un collègue a rédigé ; ici, l'action ne porte que sur ce champ, la vider
 * est une intention.
 */
function NouvellesCirconstances({ liaison, peutModifier, onEnregistre }) {
  const { t } = useTranslation('sinistres');
  const initial = liaison.nouvellesCirconstances || '';
  const [texte, setTexte] = useState(initial);
  const [envoi, setEnvoi] = useState(false);

  /* Remis à la valeur du serveur quand la ligne change : sans cela, replier puis
     déplier une AUTRE liaison réaffichait le texte de la précédente. */
  useEffect(() => { setTexte(liaison.nouvellesCirconstances || ''); },
    [liaison.liaisonTrackingId, liaison.nouvellesCirconstances]);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await liaisonService.majNouvellesCirconstances(liaison.liaisonTrackingId, texte.trim());
      toast.success(texte.trim() ? t('sinistresLies.circonstances.enregistrees') : t('sinistresLies.circonstances.effacees'));
      await onEnregistre?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('sinistresLies.circonstances.enregistrementImpossible'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
        {t('sinistresLies.circonstances.titre')}
      </div>
      {peutModifier ? (
        <>
          <Input.TextArea
            rows={2}
            maxLength={4000}
            showCount
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder={t('sinistresLies.circonstances.placeholder')}
          />
          <Button
            size="small"
            type="primary"
            loading={envoi}
            disabled={texte.trim() === initial.trim()}
            onClick={enregistrer}
            style={{ marginTop: 8, borderRadius: 8, background: GREEN, borderColor: GREEN }}
          >
            {t('sinistresLies.circonstances.enregistrer')}
          </Button>
        </>
      ) : (
        <div style={{ fontSize: 13, color: initial ? '#374151' : '#9ca3af', whiteSpace: 'pre-wrap' }}>
          {initial || t('sinistresLies.circonstances.aucune')}
        </div>
      )}
    </div>
  );
}
